import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getAllAccounts, deleteAccount } from "./AccountService";
import { apiFetch } from "../../api";
import { BASE_URL as u } from "../../../commonUrls";

const STMT_URL = `${u}/bank-statement`;
const MAX_FILE_MB = 5;

export default function AccountList() {
  const [accounts, setAccounts] = useState([]);
  const [importAccount, setImportAccount] = useState(null); // { id, accountName }
  const [file, setFile]         = useState(null);
  const [fileError, setFileError] = useState(null);
  const [preview, setPreview]   = useState(null);   // BankStatementImportResult from server
  const [checkedRefs, setCheckedRefs] = useState(new Set()); // POSSIBLE_DUPLICATE refs user chose to include
  const [previewLoading, setPreviewLoading] = useState(false);
  const [importing, setImporting]     = useState(false);
  const [result, setResult]     = useState(null);   // final import result
  const fileRef = useRef();
  const nav = useNavigate();

  useEffect(() => { load(); }, []);

  const load = async () => {
    const data = await getAllAccounts();
    setAccounts(data);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete account?")) return;
    await deleteAccount(id);
    load();
  };

  // ─── open / close ──────────────────────────────────────────
  const openImport = (account) => {
    setImportAccount({ id: account.id, accountName: account.accountName });
    resetModal();
  };

  const closeImport = () => {
    setImportAccount(null);
    resetModal();
  };

  const resetModal = () => {
    setFile(null);
    setFileError(null);
    setPreview(null);
    setCheckedRefs(new Set());
    setResult(null);
    if (fileRef.current) fileRef.current.value = "";
  };

  // ─── file selection ────────────────────────────────────────
  const handleFileChange = (e) => {
    const f = e.target.files[0] || null;
    setPreview(null);
    setCheckedRefs(new Set());
    setResult(null);
    setFileError(null);

    if (!f) { setFile(null); return; }

    if (!f.name.toLowerCase().endsWith(".csv")) {
      setFileError("Please select a CSV file (.csv).");
      setFile(null);
      return;
    }
    if (f.size > MAX_FILE_MB * 1024 * 1024) {
      setFileError(`File is too large (${(f.size / 1024 / 1024).toFixed(1)} MB). Maximum is ${MAX_FILE_MB} MB.`);
      setFile(null);
      return;
    }
    if (f.size === 0) {
      setFileError("File is empty.");
      setFile(null);
      return;
    }
    setFile(f);
  };

  // ─── preview ───────────────────────────────────────────────
  const handlePreview = async () => {
    if (!file) return;
    setPreviewLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await apiFetch(
        `${STMT_URL}/preview?accountId=${encodeURIComponent(importAccount.id)}`,
        { method: "POST", body: fd }
      );
      const data = await res.json();
      setPreview(data);
      // Pre-check: nothing checked by default (conservative — user must opt-in to possible dups)
      setCheckedRefs(new Set());
    } catch (err) {
      setPreview({ errors: [err.message] });
    } finally {
      setPreviewLoading(false);
    }
  };

  // ─── possible-duplicate checkbox toggle ───────────────────
  const toggleRef = (sourceRef) => {
    setCheckedRefs(prev => {
      const next = new Set(prev);
      if (next.has(sourceRef)) next.delete(sourceRef);
      else next.add(sourceRef);
      return next;
    });
  };

  const selectAllPossible = () => {
    const refs = possibleDupRows.map(r => r.sourceRef);
    setCheckedRefs(new Set(refs));
  };

  const clearAllPossible = () => setCheckedRefs(new Set());

  // ─── import ────────────────────────────────────────────────
  const handleImport = async () => {
    if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const forceRefs = [...checkedRefs].join(",");
      const res = await apiFetch(
        `${STMT_URL}/import?accountId=${encodeURIComponent(importAccount.id)}&forceImportRefs=${encodeURIComponent(forceRefs)}`,
        { method: "POST", body: fd }
      );
      const data = await res.json();
      setResult(data);
      load(); // refresh account balances
    } catch (err) {
      setResult({ errors: [err.message] });
    } finally {
      setImporting(false);
    }
  };

  // ─── derived state ─────────────────────────────────────────
  const rows          = preview?.rows ?? [];
  const newRows       = rows.filter(r => r.status === "NEW");
  const dupRows       = rows.filter(r => r.status === "DUPLICATE");
  const possibleDupRows = rows.filter(r => r.status === "POSSIBLE_DUPLICATE");
  const errorRows     = rows.filter(r => r.status === "ERROR");

  const totalToImport = newRows.length + checkedRefs.size;
  const hasValidationError = preview && preview.validationError;
  const hasData = rows.length > 0;

  const balanceDiffers = preview?.currentBalance != null && preview?.finalBalance != null &&
    Math.abs(Number(preview.currentBalance) - Number(preview.finalBalance)) > 0.01;

  return (
    <div className="card">
      <button className="btn btn-primary" onClick={() => nav("new")}>+ Add Account</button>

      <div className="data-table-wrap">
        <table>
          <thead>
            <tr>
              <th>Name</th><th>Type</th><th>Balance</th><th>Number</th><th>Comment</th><th>Active</th><th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {accounts.map(a => (
              <tr key={a.id}>
                <td>{a.accountName}</td>
                <td>{a.type}</td>
                <td>₹ {Number(a.currentBalance).toLocaleString("en-IN")}</td>
                <td>{a.accountNumber}</td>
                <td>{a.comment}</td>
                <td>{a.isActive ? "Yes" : "No"}</td>
                <td style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                  <button className="btn" onClick={() => nav(`edit/${a.id}`)}>Edit</button>
                  <button className="btn btn-secondary" onClick={() => openImport(a)}
                    title="Import bank statement CSV">Import</button>
                  <button className="btn btn-danger" onClick={() => handleDelete(a.id)}>Del</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* ── IMPORT MODAL ──────────────────────────────────────── */}
      {importAccount && (
        <div style={overlay} onClick={closeImport}>
          <div style={modalBox} onClick={e => e.stopPropagation()}>

            {/* Header */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 18 }}>
              <div>
                <h3 style={{ margin: 0 }}>Import Bank Statement</h3>
                <div style={{ color: "var(--text-muted)", fontSize: 13, marginTop: 2 }}>
                  Account: <strong>{importAccount.accountName}</strong>
                </div>
              </div>
              <button onClick={closeImport} style={{ background: "none", border: "none", fontSize: 22, cursor: "pointer", color: "var(--text-muted)", lineHeight: 1 }}>×</button>
            </div>

            {/* ── RESULT SCREEN ── */}
            {result && (
              <div>
                {result.validationError && (
                  <ErrorBox message={result.validationError} />
                )}
                {!result.validationError && (
                  <>
                    <div style={{ textAlign: "center", marginBottom: 20 }}>
                      <div style={{ fontSize: 40, marginBottom: 6 }}>{result.importedCount > 0 ? "✓" : "ℹ"}</div>
                      <h4 style={{ margin: 0 }}>Import Complete</h4>
                    </div>
                    <div style={{ display: "flex", gap: 20, justifyContent: "center", marginBottom: 18 }}>
                      <StatBlock label="Imported" value={result.importedCount} color="#22c55e" />
                      <StatBlock label="Skipped" value={result.skippedCount} color="#6b7280" />
                      <StatBlock label="Errors" value={result.errorCount} color="#ef4444" />
                    </div>
                    {result.finalBalance != null && (
                      <div style={{ textAlign: "center", color: "var(--text-muted)", fontSize: 13, marginBottom: 12 }}>
                        Account balance updated to{" "}
                        <strong style={{ color: "#22c55e" }}>
                          ₹{Number(result.finalBalance).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                        </strong>
                      </div>
                    )}
                    {result.errors?.length > 0 && (
                      <div style={{ ...alertBox, borderColor: "#ef4444", background: "rgba(239,68,68,0.08)", marginBottom: 12 }}>
                        {result.errors.map((e, i) => <div key={i} style={{ color: "#f87171", fontSize: 12 }}>{e}</div>)}
                      </div>
                    )}
                  </>
                )}
                <div style={{ textAlign: "center", marginTop: 16 }}>
                  <button className="btn btn-primary" onClick={closeImport}>Done</button>
                </div>
              </div>
            )}

            {/* ── UPLOAD + PREVIEW SCREEN ── */}
            {!result && (
              <>
                <p style={{ color: "var(--text-muted)", fontSize: 13, marginBottom: 14 }}>
                  Upload an Axis Bank CSV statement. Existing transactions are automatically detected and skipped.
                  Possible duplicates (same date &amp; amount, different description) are flagged for your review.
                </p>

                {/* File picker */}
                <div style={{ display: "flex", gap: 10, alignItems: "flex-start", marginBottom: 4 }}>
                  <input ref={fileRef} type="file" accept=".csv" onChange={handleFileChange}
                    style={{ flex: 1, fontSize: 13 }} />
                  <button className="btn btn-secondary" onClick={handlePreview}
                    disabled={!file || previewLoading} style={{ whiteSpace: "nowrap" }}>
                    {previewLoading ? "Parsing…" : "Preview"}
                  </button>
                </div>
                {fileError && <div style={{ color: "#f87171", fontSize: 12, marginBottom: 10 }}>{fileError}</div>}
                {file && !fileError && (
                  <div style={{ color: "var(--text-muted)", fontSize: 12, marginBottom: 10 }}>
                    {file.name} — {(file.size / 1024).toFixed(0)} KB
                  </div>
                )}

                {/* Top-level validation error from server */}
                {hasValidationError && <ErrorBox message={preview.validationError} />}

                {/* Server errors list */}
                {!hasValidationError && preview?.errors?.length > 0 && (
                  <div style={{ ...alertBox, borderColor: "#f97316", background: "rgba(249,115,22,0.08)", marginBottom: 12 }}>
                    {preview.errors.map((e, i) => <div key={i} style={{ color: "#fb923c", fontSize: 12 }}>{e}</div>)}
                  </div>
                )}

                {/* Summary chips */}
                {hasData && !hasValidationError && (
                  <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginBottom: 14, padding: "10px 14px", background: "rgba(255,255,255,0.04)", borderRadius: 8 }}>
                    <StatBlock label="New" value={newRows.length} color="#22c55e" />
                    <StatBlock label="Exact Dup." value={dupRows.length} color="#6b7280" />
                    <StatBlock label="Possible Dup." value={possibleDupRows.length} color="#f59e0b" />
                    <StatBlock label="Errors" value={errorRows.length} color="#ef4444" />
                    {preview.finalBalance != null && (
                      <StatBlock
                        label="Statement Balance"
                        value={`₹${Number(preview.finalBalance).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`}
                        color="#6366f1" />
                    )}
                  </div>
                )}

                {/* Balance drift warning */}
                {balanceDiffers && !hasValidationError && (
                  <div style={{ ...alertBox, borderColor: "#f59e0b", background: "rgba(245,158,11,0.08)", marginBottom: 14 }}>
                    <strong style={{ color: "#fbbf24" }}>Balance mismatch detected</strong>
                    <div style={{ color: "var(--text-muted)", fontSize: 12, marginTop: 4 }}>
                      Your current account balance is{" "}
                      <strong>₹{Number(preview.currentBalance).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</strong>,
                      but the statement closes at{" "}
                      <strong>₹{Number(preview.finalBalance).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</strong>.
                      Importing will update your balance to the statement figure.
                    </div>
                  </div>
                )}

                {/* POSSIBLE DUPLICATE section — user must decide */}
                {possibleDupRows.length > 0 && !hasValidationError && (
                  <div style={{ marginBottom: 16 }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                      <div style={{ fontWeight: 600, color: "#f59e0b", fontSize: 14 }}>
                        ⚠ Possible Duplicates ({possibleDupRows.length})
                      </div>
                      <div style={{ display: "flex", gap: 8 }}>
                        <button className="btn" style={{ fontSize: 11, padding: "3px 8px" }} onClick={selectAllPossible}>
                          Include all
                        </button>
                        <button className="btn" style={{ fontSize: 11, padding: "3px 8px" }} onClick={clearAllPossible}>
                          Skip all
                        </button>
                      </div>
                    </div>
                    <div style={{ color: "var(--text-muted)", fontSize: 12, marginBottom: 8 }}>
                      These rows match an existing transaction's date and amount but have a different description.
                      Check the ones you want to import anyway.
                    </div>
                    <div style={{ border: "1px solid rgba(245,158,11,0.3)", borderRadius: 8, overflow: "hidden" }}>
                      {possibleDupRows.map((row, i) => (
                        <div key={i} style={{
                          display: "flex", alignItems: "flex-start", gap: 10, padding: "10px 12px",
                          borderBottom: i < possibleDupRows.length - 1 ? "1px solid rgba(255,255,255,0.06)" : "none",
                          background: checkedRefs.has(row.sourceRef) ? "rgba(245,158,11,0.06)" : "transparent"
                        }}>
                          <input type="checkbox" checked={checkedRefs.has(row.sourceRef)}
                            onChange={() => toggleRef(row.sourceRef)}
                            style={{ marginTop: 2, flexShrink: 0, width: 15, height: 15 }} />
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ display: "flex", gap: 10, alignItems: "baseline", flexWrap: "wrap" }}>
                              <span style={{ fontSize: 12, color: "var(--text-muted)" }}>{row.rawDate}</span>
                              <span style={{ fontSize: 13, fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                                title={row.particulars}>{row.particulars || "(no description)"}</span>
                              <span style={{ fontWeight: 700, color: row.type === "INCOME" ? "#22c55e" : "#f87171", whiteSpace: "nowrap" }}>
                                {row.type === "INCOME" ? "+" : "-"}₹{Number(row.amount).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                              </span>
                            </div>
                            {row.duplicateHint && (
                              <div style={{ color: "#f59e0b", fontSize: 11, marginTop: 3 }}>
                                ⚠ {row.duplicateHint}
                              </div>
                            )}
                          </div>
                          <div style={{ fontSize: 11, color: checkedRefs.has(row.sourceRef) ? "#fbbf24" : "#6b7280", whiteSpace: "nowrap" }}>
                            {checkedRefs.has(row.sourceRef) ? "Include" : "Skip"}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Transaction table */}
                {hasData && !hasValidationError && (
                  <div style={{ maxHeight: 280, overflowY: "auto", marginBottom: 16 }}>
                    <table style={{ width: "100%", fontSize: 12, borderCollapse: "collapse" }}>
                      <thead>
                        <tr style={{ background: "rgba(255,255,255,0.05)", position: "sticky", top: 0 }}>
                          <th style={th}>Date</th>
                          <th style={th}>Particulars</th>
                          <th style={th}>Type</th>
                          <th style={{ ...th, textAlign: "right" }}>Amount</th>
                          <th style={{ ...th, textAlign: "right" }}>Balance</th>
                          <th style={th}>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rows.filter(r => r.status !== "POSSIBLE_DUPLICATE").map((row, i) => (
                          <tr key={i} style={{ borderBottom: "1px solid rgba(255,255,255,0.05)", opacity: row.status === "DUPLICATE" ? 0.5 : 1 }}>
                            <td style={td}>{row.rawDate}</td>
                            <td style={{ ...td, maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                              title={row.particulars}>{row.particulars || <em style={{ color: "var(--text-muted)" }}>—</em>}</td>
                            <td style={td}>
                              {row.type && (
                                <span style={{ color: row.type === "INCOME" ? "#22c55e" : "#f87171", fontWeight: 600 }}>
                                  {row.type === "INCOME" ? "CR" : "DR"}
                                </span>
                              )}
                            </td>
                            <td style={{ ...td, textAlign: "right" }}>
                              {row.amount != null
                                ? `₹${Number(row.amount).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`
                                : "—"}
                            </td>
                            <td style={{ ...td, textAlign: "right", color: "var(--text-muted)" }}>
                              {row.balance != null
                                ? `₹${Number(row.balance).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`
                                : "—"}
                            </td>
                            <td style={td}>
                              <StatusBadge row={row} />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* IMPORT footer */}
                {hasData && !hasValidationError && (
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", paddingTop: 8, borderTop: "1px solid rgba(255,255,255,0.08)" }}>
                    <div style={{ fontSize: 13, color: "var(--text-muted)" }}>
                      {totalToImport === 0
                        ? "Nothing new to import."
                        : <>Will import <strong style={{ color: "#22c55e" }}>{totalToImport}</strong> transaction{totalToImport !== 1 ? "s" : ""}
                          {checkedRefs.size > 0 && <> (incl. <strong style={{ color: "#f59e0b" }}>{checkedRefs.size}</strong> possible dup{checkedRefs.size !== 1 ? "s" : ""})</>}.</>
                      }
                    </div>
                    <div style={{ display: "flex", gap: 8 }}>
                      <button className="btn" onClick={closeImport}>Cancel</button>
                      {totalToImport > 0 && (
                        <button className="btn btn-primary" onClick={handleImport} disabled={importing}>
                          {importing ? "Importing…" : `Import ${totalToImport}`}
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── small components ──────────────────────────────────────────

function ErrorBox({ message }) {
  return (
    <div style={{ ...alertBox, borderColor: "#ef4444", background: "rgba(239,68,68,0.08)", marginBottom: 14 }}>
      <strong style={{ color: "#f87171" }}>Error</strong>
      <div style={{ color: "#fca5a5", fontSize: 13, marginTop: 4 }}>{message}</div>
    </div>
  );
}

function StatBlock({ label, value, color }) {
  return (
    <div style={{ textAlign: "center", minWidth: 48 }}>
      <div style={{ fontSize: 20, fontWeight: 700, color }}>{value}</div>
      <div style={{ fontSize: 11, color: "var(--text-muted)", whiteSpace: "nowrap" }}>{label}</div>
    </div>
  );
}

function StatusBadge({ row }) {
  const map = {
    NEW:               { bg: "rgba(34,197,94,0.15)",  color: "#22c55e", label: "New"      },
    DUPLICATE:         { bg: "rgba(107,114,128,0.15)", color: "#9ca3af", label: "Exact Dup"},
    POSSIBLE_DUPLICATE:{ bg: "rgba(245,158,11,0.15)", color: "#f59e0b", label: "Poss. Dup"},
    ERROR:             { bg: "rgba(239,68,68,0.15)",  color: "#ef4444", label: "Error"    },
    IMPORTED:          { bg: "rgba(99,102,241,0.15)", color: "#818cf8", label: "Imported" },
    SKIPPED:           { bg: "rgba(107,114,128,0.15)", color: "#9ca3af", label: "Skipped"  },
  };
  const s = map[row?.status] || { bg: "transparent", color: "#9ca3af", label: row?.status ?? "—" };
  return (
    <div>
      <span style={{ background: s.bg, color: s.color, padding: "2px 8px", borderRadius: 10, fontSize: 11, fontWeight: 600 }}>
        {s.label}
      </span>
      {row.parseError && (
        <div style={{ color: "#f87171", fontSize: 10, marginTop: 2 }} title={row.parseError}>
          {row.parseError.length > 40 ? row.parseError.substring(0, 40) + "…" : row.parseError}
        </div>
      )}
      {row.warning && (
        <div style={{ color: "#f59e0b", fontSize: 10, marginTop: 2 }} title={row.warning}>
          ⚠ {row.warning.length > 40 ? row.warning.substring(0, 40) + "…" : row.warning}
        </div>
      )}
    </div>
  );
}

// ─── style constants ─────────────────────────────────────────

const overlay = {
  position: "fixed", inset: 0, background: "rgba(0,0,0,0.65)",
  display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000
};
const modalBox = {
  background: "var(--surface, #1e1e2e)",
  border: "1px solid var(--border, rgba(255,255,255,0.12))",
  borderRadius: 12, padding: 24,
  maxWidth: 800, width: "95vw", maxHeight: "92vh", overflowY: "auto"
};
const alertBox = {
  border: "1px solid", borderRadius: 8, padding: "10px 14px"
};
const th = { padding: "7px 8px", textAlign: "left", fontWeight: 600, fontSize: 11, color: "var(--text-muted)", whiteSpace: "nowrap" };
const td = { padding: "5px 8px" };
